/* eslint-disable no-param-reassign */
import stopsService from '../../services/stopsService';
import companiesService from '../../services/companies';
import {
  mergeCategories, STOP_MODULES, STOP_TABS, STOPS_SHOW_ALL_LIMIT,
} from '../../constants/stopsCategories';
import {
  loadHiddenCategories,
  saveHiddenCategories,
  loadShowAreas,
  saveShowAreas,
} from '../../helpers/stopsLocalStorage';

const SEARCH_DEBOUNCE_MS = 250;
// Shorter terms match most stops, so they leave the list unfiltered.
const SEARCH_MIN_LENGTH = 2;
// The BE caps a list page without a bbox at 100 rows.
const SHOW_ALL_PAGE_SIZE = 100;
let searchDebounceHandle = null;
// Bumped on every list request so a slower, older response (e.g. from an
// earlier search keystroke) can't overwrite the list of a newer one.
let listRequestSeq = 0;

function defaultQuery() {
  return {
    q: '',
    categories: [],
    modules: [],
    campusIds: [],
    onlyAda: false,
    includeUniversal: false,
    includeArchived: false,
    sortBy: 'name',
    offset: 0,
    limit: 25,
  };
}

function otherTab(tab) {
  return tab === STOP_TABS.ON_DEMAND ? STOP_TABS.REGULAR : STOP_TABS.ON_DEMAND;
}

/**
 * Query params that make the list request exact for the active tab, so the
 * tab counts and rows are computed by the BE over the whole data set instead
 * of over whichever 25 rows happen to be loaded (VSER-14481 #1: a new on-demand
 * stop was invisible in the "On Demand" tab because the first page held only
 * scheduled stops).
 *
 * The BE only has `onlyOnDemand=true`; there is no "not on-demand" flag. But
 * on-demand stops carry no modules and the BE `modules` filter is an OR over
 * the modules CSV, so asking for every scheduled module is an exact complement.
 */
function tabListParams(tab, query) {
  // On-demand stops have no modules, so a module filter would empty the tab.
  if (tab === STOP_TABS.ON_DEMAND) return { onlyOnDemand: true, modules: [] };
  const chosen = Array.isArray(query.modules) ? query.modules : [];
  return { onlyOnDemand: false, modules: chosen.length ? chosen : [...STOP_MODULES] };
}

/** Applied filters (categories + modules + campuses + toggles); the search term is not one. */
function countActiveFilters(query) {
  const {
    categories, modules, campusIds, onlyAda, includeUniversal, includeArchived,
  } = query;
  let count = 0;
  count += (categories?.length || 0);
  count += (modules?.length || 0);
  count += (campusIds?.length || 0);
  if (onlyAda) count += 1;
  if (includeUniversal) count += 1;
  if (includeArchived) count += 1;
  return count;
}

function isFilteredQuery(query) {
  return countActiveFilters(query) > 0 || !!query.q;
}

export default {
  namespaced: true,

  state: {
    categories: [],
    // Left-panel tab (Shuttle/Vanpool vs On Demand) and the BE `total` per tab.
    tab: STOP_TABS.REGULAR,
    tabCounts: {
      [STOP_TABS.REGULAR]: null,
      [STOP_TABS.ON_DEMAND]: null,
    },
    // Authorized campuses for the Responsible Campus combo / detail label
    // (Campus Stops). Sourced from GET /v3/campuses?ownCampuses=true&allCampuses=true
    // — the admin's own campuses, the set the BE accepts (VSER-14482).
    campuses: [],
    // Company default stop radius in metres (shuttle/vanpool settings
    // `radius` = org setting STATION_RADIUS). null until loaded / when absent.
    defaultRadiusMeters: null,
    list: {
      items: [],
      offset: 0,
      limit: 25,
      total: 0,
      loading: false,
      error: null,
    },
    query: defaultQuery(),
    search: {
      query: '',
    },
    selectedStop: null,
    selectedStopLoading: false,
    routeUsage: {
      totalCount: 0,
      groups: [],
      ungrouped: [],
      loading: false,
    },
    legend: {
      hidden: loadHiddenCategories(),
      showAreas: loadShowAreas(true),
    },
  },

  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = mergeCategories(categories);
    },
    SET_CAMPUSES(state, campuses) {
      state.campuses = Array.isArray(campuses) ? campuses : [];
    },
    SET_DEFAULT_RADIUS(state, meters) {
      state.defaultRadiusMeters = Number.isFinite(meters) && meters > 0 ? meters : null;
    },
    SET_TAB(state, tab) {
      state.tab = tab === STOP_TABS.ON_DEMAND ? STOP_TABS.ON_DEMAND : STOP_TABS.REGULAR;
    },
    SET_TAB_COUNT(state, { tab, total }) {
      if (!(tab in state.tabCounts)) return;
      state.tabCounts = { ...state.tabCounts, [tab]: Number.isFinite(total) ? total : 0 };
    },
    SET_LIST_LOADING(state, loading) {
      state.list.loading = loading;
    },
    SET_LIST(state, payload) {
      const {
        items = [], offset = 0, limit = 25, total = 0, append = false,
      } = payload || {};
      // Infinite-scroll semantics: a paged fetch (offset > 0) appends onto
      // the existing list so the user's scroll position stays anchored.
      // Filter changes (setQuery) reset offset to 0 → we replace, as expected.
      state.list.items = append ? [...state.list.items, ...items] : items;
      state.list.offset = offset;
      state.list.limit = limit;
      state.list.total = total;
      state.list.error = null;
    },
    SET_LIST_ERROR(state, error) {
      state.list.error = error;
    },
    PATCH_QUERY(state, patch) {
      state.query = { ...state.query, ...patch };
    },
    RESET_QUERY(state) {
      state.query = defaultQuery();
    },
    SET_SEARCH_QUERY(state, q) {
      state.search.query = q;
    },
    CLEAR_SEARCH(state) {
      state.search.query = '';
    },
    SET_SELECTED_STOP(state, stop) {
      state.selectedStop = stop;
    },
    SET_SELECTED_STOP_LOADING(state, loading) {
      state.selectedStopLoading = loading;
    },
    SET_ROUTE_USAGE(state, payload) {
      const { totalCount = 0, groups = [], ungrouped = [] } = payload || {};
      state.routeUsage.totalCount = totalCount;
      state.routeUsage.groups = groups;
      state.routeUsage.ungrouped = ungrouped;
    },
    SET_ROUTE_USAGE_LOADING(state, loading) {
      state.routeUsage.loading = loading;
    },
    TOGGLE_LEGEND_CATEGORY(state, key) {
      if (!key) return;
      const hidden = state.legend.hidden.slice();
      const idx = hidden.indexOf(key);
      if (idx >= 0) hidden.splice(idx, 1);
      else hidden.push(key);
      state.legend.hidden = hidden;
      saveHiddenCategories(hidden);
    },
    SET_LEGEND_HIDDEN(state, hidden) {
      const list = Array.isArray(hidden) ? hidden.slice() : [];
      state.legend.hidden = list;
      saveHiddenCategories(list);
    },
    SET_SHOW_AREAS(state, value) {
      state.legend.showAreas = !!value;
      saveShowAreas(state.legend.showAreas);
    },
  },

  actions: {
    async fetchCategories({ commit, state }) {
      // First call seeds defaults; subsequent BE responses replace them.
      try {
        const response = await stopsService.getCategories();
        commit('SET_CATEGORIES', response);
      } catch (error) {
        // Falling back to defaults via mergeCategories.
        if (state.categories.length === 0) commit('SET_CATEGORIES', []);
      }
    },

    /**
     * Authorized campuses for the Responsible Campus combo and the detail
     * label. Cached after the first successful load; pass `{ force: true }`
     * after a campus stop is created, renamed or deleted — each campus stop is
     * also a campus record (VSER-14482), so the list itself changes.
     */
    async fetchCampuses({ commit, state }, { force = false } = {}) {
      if (!force && state.campuses.length > 0) return;
      try {
        const campuses = await stopsService.getOwnCampuses();
        commit('SET_CAMPUSES', campuses);
      } catch (error) {
        // Keep whatever we already had on a failed refresh.
        if (!force) commit('SET_CAMPUSES', []);
      }
    },

    /**
     * Company default radius for new stops (spec §3.4.1). Read from the
     * shuttle/vanpool settings the same way the Company Settings screen does;
     * cached after the first successful load. Failures leave the 200 m fallback.
     */
    async fetchDefaultRadius({ commit, state, rootState }) {
      if (state.defaultRadiusMeters != null) return;
      const { companyId } = rootState.companySettings || {};
      const { accountType } = rootState.userDetails || {};
      if (!companyId || !accountType) return;
      try {
        const settings = await companiesService.getSettings(accountType, 'shuttle-vanpool', companyId);
        commit('SET_DEFAULT_RADIUS', Number(settings?.radius));
      } catch (error) {
        commit('SET_DEFAULT_RADIUS', null);
      }
    },

    /**
     * `refreshCounts`: re-fetch both tab badges, e.g. after a stop is created,
     * updated or deleted. Otherwise a badge is only fetched while unknown.
     */
    async fetchList({ commit, state }, { refreshCounts = false } = {}) {
      commit('SET_LIST_LOADING', true);
      // Capture the outgoing offset / tab BEFORE awaiting so a concurrent
      // setQuery/setTab can't flip our append intent mid-flight.
      const append = (state.query.offset || 0) > 0;
      const { tab, query } = state;
      // Tab badges always show each tab's full stop count, which search and
      // filters don't change (the filtered count is shown above the list), so
      // a search costs one request. Unfiltered, the list total is the active
      // tab's badge for free.
      const filtered = isFilteredQuery(query);
      const countTabs = append && !refreshCounts ? [] : [tab, otherTab(tab)].filter((countTab) => {
        if (countTab === tab && !filtered) return false;
        return refreshCounts || state.tabCounts[countTab] == null;
      });
      listRequestSeq += 1;
      const seq = listRequestSeq;
      try {
        const listRequest = stopsService.getList({ ...query, ...tabListParams(tab, query) });
        // `limit=1` keeps the count requests cheap; a failure only leaves the
        // badge stale.
        const countRequests = countTabs.map((countTab) => stopsService
          .getList({ ...tabListParams(countTab, {}), offset: 0, limit: 1 })
          .catch(() => null));
        const [response, ...counts] = await Promise.all([listRequest, ...countRequests]);
        if (seq !== listRequestSeq) return;
        commit('SET_LIST', { ...response, append });
        if (!filtered) commit('SET_TAB_COUNT', { tab, total: response.total });
        counts.forEach((count, i) => {
          if (count) commit('SET_TAB_COUNT', { tab: countTabs[i], total: count.total });
        });
      } catch (error) {
        if (seq === listRequestSeq) commit('SET_LIST_ERROR', error?.message || 'Failed to load stops');
      } finally {
        if (seq === listRequestSeq) commit('SET_LIST_LOADING', false);
      }
    },

    /**
     * "Show all": load the rest of the current results, up to
     * STOPS_SHOW_ALL_LIMIT, in parallel pages and commit them as one append so
     * the map re-fits once instead of once per page.
     */
    async loadAll({ commit, state }) {
      const { tab, query } = state;
      const from = state.list.items.length;
      const to = Math.min(state.list.total, STOPS_SHOW_ALL_LIMIT);
      if (from >= to) return;
      const offsets = [];
      for (let offset = from; offset < to; offset += SHOW_ALL_PAGE_SIZE) offsets.push(offset);
      // Also supersedes an in-flight scroll page, which would append duplicates.
      listRequestSeq += 1;
      const seq = listRequestSeq;
      commit('SET_LIST_LOADING', true);
      try {
        const pages = await Promise.all(offsets.map((offset) => stopsService.getList({
          ...query,
          ...tabListParams(tab, query),
          offset,
          limit: Math.min(SHOW_ALL_PAGE_SIZE, to - offset),
        })));
        if (seq !== listRequestSeq) return;
        commit('SET_LIST', {
          items: pages.flatMap((page) => page.items || []),
          offset: from,
          limit: to - from,
          total: pages[pages.length - 1].total,
          append: true,
        });
      } catch (error) {
        if (seq === listRequestSeq) commit('SET_LIST_ERROR', error?.message || 'Failed to load stops');
      } finally {
        if (seq === listRequestSeq) commit('SET_LIST_LOADING', false);
      }
    },

    setTab({ commit, dispatch, state }, tab) {
      if (tab === state.tab) return Promise.resolve();
      commit('SET_TAB', tab);
      commit('PATCH_QUERY', { offset: 0 });
      return dispatch('fetchList');
    },

    setQuery({ commit, dispatch }, patch) {
      commit('PATCH_QUERY', { ...patch, offset: 0 });
      return dispatch('fetchList');
    },

    setPage({ commit, dispatch }, offset) {
      commit('PATCH_QUERY', { offset: Number(offset) || 0 });
      return dispatch('fetchList');
    },

    /**
     * Reload from the first page and refresh both tab badges: on page open and
     * after a stop is created, updated or deleted. Re-fetching at the scrolled
     * offset would append rows that are already in the list.
     */
    reloadList({ commit, dispatch }) {
      commit('PATCH_QUERY', { offset: 0 });
      return dispatch('fetchList', { refreshCounts: true });
    },

    resetQuery({ commit, dispatch }) {
      commit('RESET_QUERY');
      return dispatch('fetchList');
    },

    /** Drop the search term and every filter in one list request. */
    clearFilters({ commit, dispatch }) {
      if (searchDebounceHandle) {
        clearTimeout(searchDebounceHandle);
        searchDebounceHandle = null;
      }
      commit('CLEAR_SEARCH');
      return dispatch('resetQuery');
    },

    searchStops({ commit, dispatch, state }, q) {
      // Keep the raw value in the store (the input is bound to `searchQuery`);
      // trimming here would strip the trailing space on every keystroke, making
      // it impossible to type spaces in multi-word stop names. Only trim the
      // value we actually send to the backend.
      const raw = q || '';
      const trimmed = raw.trim();
      commit('SET_SEARCH_QUERY', raw);

      if (searchDebounceHandle) clearTimeout(searchDebounceHandle);

      if (!trimmed) {
        commit('CLEAR_SEARCH');
        if (state.query.q) dispatch('setQuery', { q: '' });
        return;
      }

      if (trimmed.length < SEARCH_MIN_LENGTH) {
        if (state.query.q) dispatch('setQuery', { q: '' });
        return;
      }

      // The list and map both render `list.items`, so the term filters them
      // through the list endpoint's `q` (matches name/address).
      searchDebounceHandle = setTimeout(() => {
        searchDebounceHandle = null;
        if (trimmed !== state.query.q) dispatch('setQuery', { q: trimmed });
      }, SEARCH_DEBOUNCE_MS);
    },

    clearSearch({ commit, dispatch, state }) {
      if (searchDebounceHandle) {
        clearTimeout(searchDebounceHandle);
        searchDebounceHandle = null;
      }
      commit('CLEAR_SEARCH');
      if (state.query.q) dispatch('setQuery', { q: '' });
    },

    async selectStop({ commit }, id) {
      if (!id) {
        commit('SET_SELECTED_STOP', null);
        commit('SET_ROUTE_USAGE', { totalCount: 0, groups: [], ungrouped: [] });
        return;
      }

      commit('SET_SELECTED_STOP_LOADING', true);
      commit('SET_ROUTE_USAGE_LOADING', true);
      try {
        const [stop, routes] = await Promise.all([
          stopsService.getStop(id),
          stopsService.getStopRoutes(id),
        ]);
        commit('SET_SELECTED_STOP', stop);
        commit('SET_ROUTE_USAGE', routes);
      } catch (error) {
        commit('SET_SELECTED_STOP', null);
        commit('SET_ROUTE_USAGE', { totalCount: 0, groups: [], ungrouped: [] });
      } finally {
        commit('SET_SELECTED_STOP_LOADING', false);
        commit('SET_ROUTE_USAGE_LOADING', false);
      }
    },

    clearSelection({ commit }) {
      commit('SET_SELECTED_STOP', null);
      commit('SET_ROUTE_USAGE', { totalCount: 0, groups: [], ungrouped: [] });
    },

    toggleLegend({ commit }, key) {
      commit('TOGGLE_LEGEND_CATEGORY', key);
    },

    setLegendHidden({ commit }, hidden) {
      commit('SET_LEGEND_HIDDEN', hidden);
    },

    toggleShowAreas({ commit, state }) {
      commit('SET_SHOW_AREAS', !state.legend.showAreas);
    },

    setShowAreas({ commit }, value) {
      commit('SET_SHOW_AREAS', value);
    },
  },

  getters: {
    categories: (state) => state.categories,
    campuses: (state) => state.campuses,
    defaultRadiusMeters: (state) => state.defaultRadiusMeters,
    tab: (state) => state.tab,
    tabCounts: (state) => state.tabCounts,
    /** Stops in the company across both tabs (drives the page-level empty state). */
    totalStops: (state) => Object.values(state.tabCounts).reduce((sum, n) => sum + (n || 0), 0),
    availableCategories: (state) => state.categories.filter((c) => c.available !== false),
    categoryByKey: (state) => {
      const map = new Map(state.categories.map((c) => [c.key, c]));
      return (key) => map.get(key) || null;
    },
    listItems: (state) => state.list.items,
    listLoading: (state) => state.list.loading,
    listTotal: (state) => state.list.total,
    listEmpty: (state) => !state.list.loading && state.list.items.length === 0,
    listError: (state) => state.list.error,
    query: (state) => state.query,
    legend: (state) => state.legend,
    selectedStop: (state) => state.selectedStop,
    selectedStopLoading: (state) => state.selectedStopLoading,
    routeUsage: (state) => state.routeUsage,
    searchQuery: (state) => state.search.query,

    /** Stops left after the legend's hidden category list is applied. */
    visibleStops: (state) => {
      const hidden = new Set(state.legend.hidden);
      if (hidden.size === 0) return state.list.items;
      return state.list.items.filter((s) => {
        const key = s.onDemand ? 'ON_DEMAND' : s.category;
        if (!key) return true;
        return !hidden.has(key);
      });
    },

    /**
     * Number of applied filters (categories + modules + campuses + toggles).
     * Mirrors the tags the filter combo renders; the view uses it to tell
     * "no stops at all" apart from "nothing matches these filters".
     */
    activeFilterCount: (state) => countActiveFilters(state.query),
    /** A search term or any filter is narrowing the list. */
    isFiltering: (state) => isFilteredQuery(state.query),
  },
};

/* eslint-enable no-param-reassign */
